<template>
  <div class="h-screen px-0 pt-0 pb-0 mx-0 my-0 box-border overflow-hidden">
    <v-row class="h-screen" no-gutters>
      <v-col class="lg:flex lg:flex-col lg:justify-center px-0 hidden relative" cols="12" lg="8">
        <LoginCarousel />
        <!-- Vertical divider -->
        <div class="absolute right-0 top-0 bottom-0 w-[1px] bg-gray-400"></div>
      </v-col>

      <v-col cols="12" lg="4" class="bg-white h-screen overflow-y-auto">
        <v-container>
          <div class="flex justify-end">
            <span> version {{ versionNumber }} </span>
          </div>
          <v-row class="flex-grow justify-center items-center">
            <v-col cols="12" sm="10" class="flex justify-center">
              <img src="@/assets/img/CW_DEFAULT.png" class="w-32" alt="Logo" />
            </v-col>
          </v-row>
          <!-- Global Switch Option for Iframe & Webview -->
          <div class="d-flex justify-center mt-6">
            <span
              class="text-primary cursor-pointer hover-effect text-blue-500 underline text-center"
              @click="switchToMobileApp"
            >
              Login as Employee
            </span>
          </div>
          <v-row class="flex-grow justify-center items-center mt-12">
            <v-col cols="12" sm="10">
              <!-- Toggle between Login and Signup -->
              <div class="d-flex mb-4">
                <v-btn-toggle v-model="authMode" mandatory class="w-100">
                  <v-btn
                    outlined
                    small
                    :value="'login'"
                    :color="authMode === 'login' ? 'primary' : undefined"
                    class="flex-grow-1 px-4 sm:px-12 py-4"
                    :class="{ 'primary white--text': authMode === 'login' }"
                  >
                    <span :class="{ 'white--text': authMode === 'login' }">Login</span>
                  </v-btn>
                  <v-btn
                    outlined
                    small
                    :value="'signup'"
                    :color="authMode === 'signup' ? 'primary' : undefined"
                    class="flex-grow-1 px-4 sm:px-12 py-4"
                    :class="{ 'primary white--text': authMode === 'signup' }"
                  >
                    <span :class="{ 'white--text': authMode === 'signup' }">Sign Up</span>
                  </v-btn>
                </v-btn-toggle>
              </div>

              <!-- Signup Component -->
              <SignupComponent
                v-if="authMode === 'signup'"
                @signup-complete="handleSignupComplete"
                @google-login="handleGoogleLoginSuccess"
              />

              <!-- Login Component (existing code) -->
              <template v-if="authMode === 'login'">
                <!-- Welcome Message with Transition -->
                <transition name="fade-up" mode="out-in">
                  <h4 class="font-bold text-2xl mb-6" :key="currentStep">
                    {{ welcomeMessage }}
                  </h4>
                </transition>

                <!-- Step 1: Phone Number -->
                <div v-if="currentStep === 'phone'" key="phone" class="login-step">
                  <v-text-field
                    v-model="form.mobile"
                    outlined
                    label="Username (Mobile / Email / Login ID) *"
                    placeholder="Enter mobile, email or login ID"
                    class="mb-2"
                    ref="phoneInput"
                    @input="checkNumberValidity"
                    @keyup.enter="validateAndNext"
                  ></v-text-field>
                  <div class="d-flex justify-end mt-4">
                    <v-btn
                      color="primary"
                      @click="validateAndNext"
                      :disabled="!isNumberValid"
                      :loading="extUserLoaing"
                      :block="$vuetify.breakpoint.smAndDown"
                      >Proceed
                      <v-icon>mdi-arrow-right</v-icon>
                    </v-btn>
                  </div>

                  <!-- Show loader during Google login process -->
                  <div v-if="isGoogleLoginLoading" class="d-flex justify-center my-4">
                    <v-progress-circular
                      indeterminate
                      color="primary"
                      size="32"
                    ></v-progress-circular>
                    <span class="ml-2">Processing login...</span>
                  </div>

                  <!-- Show Google login button when not loading -->
                  <GoogleLogin
                    v-else
                    @google-login-success="handleGoogleLoginSuccess"
                    @guest-login="handleGuestLogin"
                  />
                </div>

                <!-- Step 1.5: OTP Channel Selection -->
                <div v-if="currentStep === 'channel-select'" key="channel-select" class="login-step">
                  <div class="mb-4 text-center">
                    <p class="text-body-2 text-grey-darken-1">Choose how you want to receive your verification code</p>
                  </div>

                  <!-- Mobile & Country Selector -->
                  <v-card outlined class="pa-3 mb-4 rounded-lg bg-grey-lighten-4">
                    <div class="d-flex align-center gap-2">
                      <v-select
                        v-model="selectedCountry"
                        :items="countries"
                        item-text="name"
                        item-value="code"
                        dense
                        hide-details
                        style="max-width: 140px;"
                        outlined
                      >
                        <template v-slot:selection="{ item }">
                          <span>{{ item.flag }} {{ item.code }}</span>
                        </template>
                        <template v-slot:item="{ item }">
                          <span>{{ item.flag }} {{ item.name }} ({{ item.code }})</span>
                        </template>
                      </v-select>
                      <v-text-field
                        :value="form.mobile"
                        readonly
                        dense
                        hide-details
                        outlined
                        label="Phone Number"
                      ></v-text-field>
                    </div>
                  </v-card>

                  <!-- Warning banner for non-Indian numbers -->
                  <v-alert
                    v-if="!isSmsAvailable"
                    type="warning"
                    dense
                    outlined
                    class="text-caption mb-4"
                  >
                    ⚠️ SMS is unavailable for {{ selectedCountry }}. Please choose WhatsApp or Email.
                  </v-alert>

                  <!-- Delivery Channel Options -->
                  <div class="d-flex flex-column gap-3 mb-6">
                    <!-- SMS Option -->
                    <v-card
                      outlined
                      @click="isSmsAvailable && (selectedChannel = 'sms')"
                      :class="[
                        'pa-3 rounded-lg d-flex align-center justify-space-between transition-all',
                        !isSmsAvailable
                          ? 'bg-grey-lighten-3 text-grey-darken-1 cursor-not-allowed opacity-50'
                          : selectedChannel === 'sms'
                          ? 'blue lighten-5 border-blue'
                          : 'cursor-pointer'
                      ]"
                    >
                      <div class="d-flex align-center gap-3">
                        <v-icon color="indigo">mdi-message-text-outline</v-icon>
                        <div>
                          <div class="font-weight-bold text-body-2">Send via SMS</div>
                          <div class="text-caption text-grey-darken-1" v-if="!isSmsAvailable">Not available outside India</div>
                          <div class="text-caption text-indigo-darken-1" v-else>Instant SMS message to phone</div>
                        </div>
                      </div>
                      <v-icon v-if="selectedChannel === 'sms' && isSmsAvailable" color="indigo">mdi-check-circle</v-icon>
                    </v-card>

                    <!-- WhatsApp Option -->
                    <v-card
                      outlined
                      @click="isWhatsappAvailable && (selectedChannel = 'whatsapp')"
                      :class="[
                        'pa-3 rounded-lg d-flex align-center justify-space-between transition-all',
                        !isWhatsappAvailable
                          ? 'bg-grey-lighten-3 text-grey-darken-1 cursor-not-allowed opacity-50'
                          : selectedChannel === 'whatsapp'
                          ? 'teal lighten-5 border-teal'
                          : 'cursor-pointer'
                      ]"
                    >
                      <div class="d-flex align-center gap-3">
                        <v-icon color="teal">mdi-whatsapp</v-icon>
                        <div>
                          <div class="font-weight-bold text-body-2">Send on WhatsApp</div>
                          <div class="text-caption text-grey-darken-1" v-if="!isWhatsappAvailable">Not configured in organization</div>
                          <div class="text-caption text-teal-darken-1" v-else>Official WhatsApp OTP template</div>
                        </div>
                      </div>
                      <v-icon v-if="selectedChannel === 'whatsapp' && isWhatsappAvailable" color="teal">mdi-check-circle</v-icon>
                    </v-card>

                    <!-- Email Option -->
                    <v-card
                      v-if="isEmailAvailable"
                      outlined
                      @click="selectedChannel = 'email'"
                      :class="[
                        'pa-3 rounded-lg d-flex align-center justify-space-between transition-all cursor-pointer',
                        selectedChannel === 'email'
                          ? 'blue lighten-5 border-blue'
                          : ''
                      ]"
                    >
                      <div class="d-flex align-center gap-3">
                        <v-icon color="blue">mdi-email-outline</v-icon>
                        <div>
                          <div class="font-weight-bold text-body-2">Send on Email</div>
                          <div class="text-caption text-blue-darken-1 font-weight-medium" v-if="maskedEmail">Send code to {{ maskedEmail }}</div>
                          <div class="text-caption text-blue-darken-1" v-else>Email verification code</div>
                        </div>
                      </div>
                      <v-icon v-if="selectedChannel === 'email'" color="blue">mdi-check-circle</v-icon>
                    </v-card>
                  </div>

                  <div class="d-flex justify-space-between gap-3">
                    <v-btn color="secondary" @click="currentStep = 'phone'">Back</v-btn>
                    <v-btn color="primary" @click="confirmChannelAndSendOTP" :loading="isLoading">Send OTP <v-icon right>mdi-arrow-right</v-icon></v-btn>
                  </div>
                </div>

                <!-- Step 2: Passcode -->
                <div v-if="currentStep === 'passcode'" key="passcode" class="login-step">
                  <div class="user-info d-flex align-center mb-4">
                    <v-avatar color="primary" size="36" class="mr-3">
                      <span class="white--text">{{ userInitials }}</span>
                    </v-avatar>
                    <div>
                      <div class="text-body-1 font-weight-medium">
                        {{ maskNumber(form.mobile) }}
                      </div>
                      <a @click="resetToFirstStep" class="text-caption text-primary cursor-pointer"
                        >Change Username</a
                      >
                    </div>
                  </div>

                  <PasscodeInput
                    v-model="form.passCode"
                    :autoFocus="true"
                    @keyup.enter.native="login"
                  />

                  <v-divider class="my-4"></v-divider>

                  <div
                    class="d-flex flex-col sm:flex-row justify-space-between align-stretch sm:align-center gap-3"
                  >
                    <span
                      class="text-primary cursor-pointer hover-effect text-blue-500 underline text-center sm:text-left"
                      @click="forgotPasscode"
                    >
                      Forgot Passcode?
                    </span>
                    <v-btn
                      color="primary"
                      @click="login"
                      :disabled="form.passCode?.length !== 6"
                      :loading="isLoading"
                      :block="$vuetify.breakpoint.smAndDown"
                      >Login
                      <v-icon>mdi-login</v-icon>
                    </v-btn>
                  </div>
                </div>

                <!-- Step 3: OTP -->
                <div v-if="currentStep === 'otp'" key="otp" class="login-step">
                  <div class="mb-4 text-center">
                    <p class="text-body-2 text-grey-darken-1">
                      We've sent a verification code to {{ maskNumber(form.mobile) }}
                    </p>
                  </div>

                  <PasscodeInput
                    v-model="form.otp"
                    :otpField="4"
                    :autoFocus="true"
                    @keyup.enter.native="verifyOTP"
                  />
                  <v-divider class="my-4"></v-divider>
                  <div class="text-center mb-4">
                    <p v-if="otpCounter > 0" class="text-caption">
                      Resend OTP in <span class="font-weight-bold">{{ otpCounter }}s</span>
                    </p>
                    <span
                      v-else
                      class="text-primary cursor-pointer hover-effect"
                      @click="resendOTP"
                    >
                      Resend OTP
                    </span>
                  </div>

                  <div class="d-flex flex-col-reverse sm:flex-row justify-between gap-3 mt-2">
                    <v-btn
                      color="secondary"
                      @click="currentStep = 'passcode'"
                      :block="$vuetify.breakpoint.smAndDown"
                    >
                      Back
                    </v-btn>
                    <v-btn
                      color="primary"
                      @click="verifyOTP"
                      :disabled="form.otp?.length !== 4"
                      :loading="isLoading"
                      elevation="2"
                      class="px-8"
                      :block="$vuetify.breakpoint.smAndDown"
                    >
                      Verify
                    </v-btn>
                  </div>
                  <!-- <div class="d-flex justify-center mt-2"></div> -->
                </div>

                <!-- Step 4: Set New Passcode -->
                <div v-if="currentStep === 'set-passcode'" key="set-passcode" class="login-step">
                  <div class="mb-4 text-center">
                    <p class="text-body-2 text-grey-darken-1">Set a new 6-digit passcode</p>
                  </div>
                  <PasscodeInput
                    v-model="form.setPasscode"
                    :autoFocus="true"
                    @keyup.enter.native="setNewPasscode"
                  />
                  <v-divider class="my-4"></v-divider>
                  <div class="mb-4 text-center">
                    <p class="text-body-2 text-grey-darken-1">Confirm passcode</p>
                  </div>
                  <PasscodeInput
                    v-model="form.confirmPasscode"
                    @keyup.enter.native="setNewPasscode"
                  />

                  <div class="d-flex flex-col gap-2 justify-center">
                    <v-btn
                      color="primary"
                      @click="setNewPasscode"
                      :disabled="!canSetPasscode"
                      :loading="isLoading"
                      elevation="2"
                      class="px-8"
                      :block="$vuetify.breakpoint.smAndDown"
                    >
                      Set Passcode
                    </v-btn>
                    <v-btn
                      color="secondary"
                      @click="currentStep = 'passcode'"
                      :block="$vuetify.breakpoint.smAndDown"
                    >
                      Back
                    </v-btn>
                  </div>
                </div>
              </template>

              <!-- Toast Messages -->
              <div v-for="(alert, index) in alerts" :key="index" class="my-4">
                <v-alert dense dismissible text :type="alert.type" @input="removeAlert(index)">
                  {{ alert.message }}
                </v-alert>
              </div>
            </v-col>
          </v-row>
        </v-container>
        <div class="fixed bottom-4 right-4 z-50">
          <v-btn
            icon
            :color="$vuetify.theme.currentTheme.primary"
            @click="openComplaintCard"
            class="shadow-lg"
          >
            <v-icon>mdi-comment-processing-outline</v-icon>
          </v-btn>
          <QueryComponent v-if="isComplaintOpen" @handleCloseComplaint="openComplaintCard" />
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import QueryComponent from '@/components/queryComponent.vue'
// import Designer from '@/assets/img/Designer.svg'
// import leftImage from '@/assets/img/LeftImage.svg'
// import analysisImage from '@/assets/img/Market_analysis.svg'
import Designer from '@/assets/img/image1.png'
import leftImage from '@/assets/img/image2.png'
import analysisImage from '@/assets/img/image3.png'
import PasscodeInput from '@/components/PasscodeInput.vue'
import SignupComponent from '@/components/Workforce/SignupComponent.vue'
import GoogleLogin from '@/components/GoogleLogin.vue'
import { maskPhoneNumber } from '@/utils/common'
import { version } from '../../version'
import LoginCarousel from '@/components/Workforce/LoginCarousel.vue'

export default {
  name: 'LoginPage',
  layout: 'newLayout',
  components: {
    QueryComponent,
    PasscodeInput,
    SignupComponent,
    GoogleLogin,
    LoginCarousel
  },
  data() {
    return {
      authMode: 'login', // login, signup
      currentStep: 'phone', // phone, passcode, otp, set-passcode
      userName: '',
      currentSlide: 0,
      otpCounter: 0,
      otpTimer: null,
      isLoading: false,
      isComplaintOpen: false,
      showPasscode: false,
      versionNumber: version,
      extUserLoaing: false,
      is_google_logged_in: null,
      form: {
        mobile: null,
        passCode: null,
        otp: null,
        setPasscode: null,
        confirmPasscode: null,
        username: null
      },
      alerts: [],
      isNumberValid: false,
      isEmail: false,
      isLoginId: false,
      isGoogleLoginLoading: false,
      selectedCountry: '+91',
      selectedChannel: 'sms',
      userEmail: '',
      allowWhatsapp: false,
      allowEmail: true,
      countries: [
        { code: '+91', name: 'India', flag: '🇮🇳' },
        { code: '+880', name: 'Bangladesh', flag: '🇧🇩' },
        { code: '+94', name: 'Sri Lanka', flag: '🇱🇰' },
        { code: '+977', name: 'Nepal', flag: '🇳🇵' },
        { code: '+1', name: 'United States', flag: '🇺🇸' }
      ]
    }
  },
  computed: {
    isIndianNumberByDigit() {
      const cleanNum = this.form.mobile ? String(this.form.mobile).trim().replace(/\D/g, '') : ''
      if (cleanNum.length === 10) {
        return /^[6-9]\d{9}$/.test(cleanNum)
      }
      return false
    },
    isIndiaSelected() {
      return this.selectedCountry === '+91' && this.isIndianNumberByDigit
    },
    isSmsAvailable() {
      return this.isIndiaSelected
    },
    maskedEmail() {
      const emailVal = this.userEmail || (this.form.username && this.form.username.includes('@') ? this.form.username : '')
      if (!emailVal || !emailVal.includes('@')) return ''
      const [name, domain] = emailVal.split('@')
      if (name.length <= 2) {
        return `${name.charAt(0)}***@${domain}`
      }
      return `${name.charAt(0)}***${name.charAt(name.length - 1)}@${domain}`
    },
    isWhatsappAvailable() {
      return this.allowWhatsapp === true
    },
    isEmailAvailable() {
      const emailVal = this.userEmail || (this.form.username && this.form.username.includes('@') ? this.form.username : '')
      const hasValidEmail = Boolean(emailVal && emailVal.trim().length > 0)
      return this.allowEmail !== false && hasValidEmail
    },
    isInIframe() {
      try {
        return window.self !== window.top
      } catch (e) {
        return true
      }
    },
    isInWebview() {
      if (typeof window === 'undefined') return false
      const userAgent = window.navigator.userAgent || window.navigator.vendor || window.opera
      return (
        /wv/i.test(userAgent) ||
        /WebView/i.test(userAgent) ||
        (/iPhone|iPod|iPad/i.test(userAgent) && !/Safari/i.test(userAgent)) ||
        (/Android/i.test(userAgent) && /Version\/[0-9.]+/i.test(userAgent))
      )
    },
    showLoginAsEmployee() {
      return this.isInIframe || this.isInWebview
    },
    slides() {
      return [
        {
          image: Designer,
          headerText: 'Best Employee Experience Platform.',
          subText:
            "Don't limit yourself. Many people limit themselves to what they think they can do. You can go as far as your mind lets you. You achieve what you believe."
        },
        {
          image: leftImage,
          headerText: 'Finally a place where it all comes together',
          subText:
            'Employees like you can even make Mondays a joy. Thanks for your hard work and super attitude.'
        },
        {
          image: analysisImage,
          headerText: 'Focus on the work that matters',
          subText: "Business opportunities are like buses, there's always another one coming."
        }
      ]
    },
    welcomeMessage() {
      switch (this.currentStep) {
        case 'phone':
          return 'Login'
        case 'passcode':
          return this.userName ? `Welcome back, ${this.userName}` : 'Enter your passcode'
        case 'otp':
          return 'Verify your identity'
        case 'set-passcode':
          return 'Create new passcode'
        default:
          return 'Welcome'
      }
    },
    userInitials() {
      if (this.userName) {
        const nameParts = this.userName.split(' ')
        if (nameParts && nameParts.length > 1) {
          return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
        }
        return nameParts[0][0].toUpperCase()
      }
      return ''
    },
    canSetPasscode() {
      if (
        this.form.setPasscode?.length === 6 &&
        this.form.confirmPasscode?.length === 6 &&
        this.form.setPasscode !== this.form.confirmPasscode
      ) {
        this.showToast('Confirm password and New password are not same', 'error')
      }
      return (
        this.form.setPasscode &&
        this.form.confirmPasscode &&
        this.form.setPasscode.length === 6 &&
        this.form.setPasscode === this.form.confirmPasscode
      )
    }
  },
  watch: {
    currentStep() {
      this.form.passCode = null
      this.form.setPasscode = null
      this.form.confirmPasscode = null
      this.form.otp = null
      if (this.currentStep === 'phone') {
        this.$nextTick(() => {
          this.$refs.phoneInput.focus()
        })
      }
      // this.checkNumberValidity()
    }
  },
  created() {
    const userData = JSON.parse(localStorage.getItem('user'))
    const first_name = userData?.first_name || userData?.user?.first_name
    const mobile = userData?.mobile || userData?.user?.mobile
    const email = userData?.email || userData?.user?.email
    const username = userData?.username || userData?.user?.username || mobile || email

    if (userData && first_name && (mobile || email || username)) {
      this.userName = `${first_name || ''} ${
        userData?.last_name || userData?.user?.last_name || ''
      }`.trim()
      this.isEmail = userData?.isEmail || userData?.user?.isEmail || false
      this.isLoginId = userData?.isLoginId || userData?.user?.isLoginId || false

      this.form.mobile = this.isEmail || this.isLoginId ? username : mobile
      this.form.username = username
      this.currentStep = 'passcode'
    } else {
      localStorage.clear()
    }
  },
  methods: {
    switchToMobileApp() {
      const mobileUrl = process.env.VUE_APP_MOBILE_LOGIN_URL || 'https://uatcualityapp.myfinfi.com'
      window.open(mobileUrl, '_blank')

      try {
        window.parent.postMessage({ type: 'NAVIGATE_TO_MOBILE_LOGIN' }, '*')
      } catch (e) {
        console.error(e)
      }
    },
    maskNumber(num) {
      if (!num) return
      return maskPhoneNumber(String(num))
    },
    showToast(message, type = 'info') {
      const alert = { message, type }
      this.alerts.push(alert)
      setTimeout(() => {
        this.removeAlert(this.alerts.indexOf(alert))
      }, 10000)
    },
    removeAlert(index) {
      if (index > -1) {
        this.alerts.splice(index, 1)
      }
    },
    handleSignupComplete(data) {
      // Handle both old and new formats
      if (typeof data === 'string') {
        // Old format - just mobile number
        this.authMode = 'login'
        this.form.mobile = data
        this.form.username = data
        this.currentStep = 'passcode'
      } else if (typeof data === 'object' && data.mobile && data.passcode) {
        // New format - mobile and passcode for automatic login
        this.form.mobile = data.mobile
        this.form.username = data.mobile || data.email
        this.form.passCode = data.passcode
        // this.currentStep = 'passcode'
        if (data?.organization_code) {
          localStorage.setItem('organization_code', data.organization_code)
        }
        // Set flag indicating this is a new organization that needs setup
        localStorage.setItem('needsInitialSetup', 'true')
        // Automatically login after a short delay
        setTimeout(() => {
          this.login()
        }, 1000)
      }
      // else if (typeof data === 'object' && data.google_token) {
      //   this.handleGoogleLoginSuccess({ token: data.google_token })
      // }
    },
    validateAndNext() {
      if (!this.form.mobile) {
        this.showToast('Please enter your email, phone number or login ID', 'error')
        return
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (emailRegex.test(this.form.mobile)) {
        console.log('Email detected, Google login option available')
      }

      this.isLoading = true
      this.extUserLoaing = true

      this.$axios
        .post('/cualitywork/ext-user-details', { username: this.form.username })
        .then((res) => {
          const data = res.data
          this.is_google_logged_in = data?.is_google_logged_in
          this.userEmail = data.email || ''
          this.allowWhatsapp = data.allow_whatsapp === true
          this.allowEmail = data.allow_email !== false
          if (data.status) {
            if (data.mobile) {
              this.form.mobile = data.mobile
            }
            const userDetails = {
              user: {
                first_name: data.first_name,
                last_name: data.last_name,
                mobile: this.form.mobile,
                profile_image: data.profile_image,
                username: this.form.username,
                isEmail: this.isEmail,
                isLoginId: this.isLoginId,
                email: this.userEmail
              },
              role: data.role || []
            }
            this.userName = `${data.first_name || ''} ${data.last_name || ''}`.trim()
            if (data.organization_code) {
              localStorage.setItem('organization_code', data.organization_code)
            }
            localStorage.setItem('user', JSON.stringify(userDetails))
            console.log({ userDetails })
            if (!data.skip_otp) {
              if (data.is_google_logged_in) {
                this.showToast('Your account is created with google sign-in', 'error')
                this.extUserLoaing = false
                return
              }
              // Auto-select channel based on country & configuration
              if (this.isSmsAvailable) {
                this.selectedChannel = 'sms'
              } else if (this.isWhatsappAvailable) {
                this.selectedChannel = 'whatsapp'
              } else if (this.isEmailAvailable) {
                this.selectedChannel = 'email'
              }
              this.currentStep = 'channel-select'
            } else {
              this.currentStep = 'passcode'
            }
          } else {
            this.showToast(
              this.isLoginId
                ? 'Invalid login ID'
                : this.isEmail
                ? 'Invalid email'
                : 'Invalid phone number',
              'error'
            )
          }
        })
        .catch((err) => {
          console.error(err)
          this.showToast(
            this.isLoginId
              ? 'Failed to validate login ID'
              : this.isEmail
              ? 'Failed to validate email'
              : 'Failed to validate phone number',
            'error'
          )
        })
        .finally(() => {
          this.isLoading = false
          this.extUserLoaing = false
        })
    },

    async login() {
      if (!this.form.passCode) {
        this.showToast('Please enter your passcode', 'error')
        return
      }

      this.isLoading = true
      this.$axios
        .post('/auth/v3/login', {
          username: this.form.username,
          passcode: this.form.passCode,
          organization_code: localStorage.getItem('organization_code')
        })
        .then(async (res) => {
          // if (res.data.access_token) {
          this.$storage.setUniversal('isAuthenticated', 'true')
          await this.setUser()
          await this.fetchLoginUserId()

          if (
            this.$storage.getUniversal('user').role.includes('WFM_ADMIN') ||
            this.$storage.getUniversal('user').role.includes('WFM_ACCESS')
          ) {
            const needsInitialSetup = localStorage.getItem('needsInitialSetup')
            const hasCompletedSetup = this.$storage.getUniversal('hasCompletedSetup')

            if (needsInitialSetup === 'true' && !hasCompletedSetup) {
              this.$router.replace('/workforce/setup')
            } else {
              this.$router.replace('/workforce/dashboard')
            }
          } else {
            this.showToast('You are not authorized', 'error')
            localStorage.clear()
          }
          // }
        })
        .catch((err) => {
          console.error(err)
          this.showToast('Invalid credentials', 'error')
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    forgotPasscode() {
      // Auto-select channel based on country & configuration
      if (this.isSmsAvailable) {
        this.selectedChannel = 'sms'
      } else if (this.isWhatsappAvailable) {
        this.selectedChannel = 'whatsapp'
      } else if (this.isEmailAvailable) {
        this.selectedChannel = 'email'
      }
      this.currentStep = 'channel-select'
    },
    resetToFirstStep() {
      this.currentStep = 'phone'
      localStorage.clear()
    },
    confirmChannelAndSendOTP() {
      if (this.selectedChannel === 'sms' && !this.isSmsAvailable) {
        this.showToast('SMS is available only for Indian (+91) numbers starting with 6, 7, 8, or 9', 'error')
        return
      }
      if (this.selectedChannel === 'email' && !this.isEmailAvailable) {
        this.showToast('Customer email is not registered', 'error')
        return
      }
      this.sendOTP()
      this.currentStep = 'otp'
    },

    sendOTP() {
      this.isLoading = true
      const payload = {
        mobile: Number(this.form.mobile),
        channel: this.selectedChannel,
        notification_channel: this.selectedChannel,
        type: this.selectedChannel,
        country_code: this.selectedCountry,
        email: this.userEmail,
        whatsapp_config: {
          template_name: 'finfi_otp_login_verification',
          language: 'en',
          header: 'Verification Code',
          body: '{{otp}} is your verification code. For your security, do not share this code.'
        },
        email_config: {
          subject: 'FinFi Passcode Verification Code',
          template_id: 'FINFI_OTP_EMAIL_TEMPLATE',
          html_template: `<div style="font-family: Arial, sans-serif; padding: 20px; color: #333; max-width: 500px; margin: 0 auto; border: 1px solid #e0e0e0; border-radius: 12px;"><h2 style="color: #4f46e5; text-align: center;">FinFi Verification Code</h2><p>Hello,</p><p>Your verification OTP code for logging into FinFi is:</p><div style="text-align: center; margin: 24px 0;"><span style="font-size: 32px; font-weight: bold; letter-spacing: 6px; color: #4f46e5; background: #f3f4f6; padding: 12px 24px; border-radius: 8px; display: inline-block;">{{otp}}</span></div><p style="font-size: 13px; color: #6b7280;">This code is valid for 5 minutes.</p></div>`
        }
      }
      this.$axios
        .post('/cualitywork/send-login-otp', payload)
        .then(() => {
          this.showToast(`OTP sent via ${this.selectedChannel}!`, 'info')
          this.startOtpCounter()
        })
        .catch((err) => {
          console.error(err)
          const errMessage = err?.response?.data?.message || 'Failed to send OTP'
          this.showToast(errMessage, 'error')
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    resendOTP() {
      if (this.otpCounter > 0) return
      this.sendOTP()
    },

    startOtpCounter() {
      this.otpCounter = 60
      clearInterval(this.otpTimer)
      this.otpTimer = setInterval(() => {
        if (this.otpCounter > 0) {
          this.otpCounter--
        } else {
          clearInterval(this.otpTimer)
        }
      }, 1000)
    },

    verifyOTP() {
      if (!this.form.otp) {
        this.showToast('Please enter OTP', 'error')
        return
      }
      this.isLoading = true
      this.$axios
        .post('/auth/v3/verify-login-otp', {
          mobile: this.form.mobile,
          otp: this.form.otp,
          organization_code: localStorage.getItem('organization_code')
        })
        .then((res) => {
          if (res.data) {
            // this.tempToken = res.data?.access_token
            this.setUser()
            this.currentStep = 'set-passcode'
          }
        })
        .catch((err) => {
          console.error(err)
          this.showToast('Invalid OTP', 'error')
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    setNewPasscode() {
      if (this.form.setPasscode !== this.form.confirmPasscode) {
        this.showToast('Passcodes do not match', 'error')
        return
      }

      if (this.form.setPasscode.length !== 6) {
        this.showToast('Passcode must be 6 digits', 'error')
        return
      }

      this.isLoading = true
      this.$axios
        .post(
          '/cualitywork/update-passcode',
          { passcode: this.form.setPasscode }
          // { headers: { Authorization: this.tempToken } }
        )
        .then((res) => {
          if (res.status === 200) {
            this.showToast('Passcode set successfully', 'success')
            this.form.passCode = this.form.setPasscode
            this.form.setPasscode = null
            this.form.confirmPasscode = null
            this.currentStep = 'passcode'
          }
        })
        .catch((err) => {
          console.error(err)
          this.showToast('Failed to set passcode', 'error')
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    async setUser() {
      try {
        const res = await this.$axios.get('/cualitywork/auth-user')
        const existingUser = this.$storage.getUniversal('user') || {}

        const mergedData = {
          ...existingUser,
          ...res.data,
          isEmail: this.isEmail
        }

        if (existingUser.user && res.data.user) {
          mergedData.user = { ...existingUser.user, ...res.data.user }
          if (existingUser.user.profile_image && !res.data.user.profile_image) {
            mergedData.user.profile_image = existingUser.user.profile_image
          }
        }

        this.$storage.setUniversal('user', mergedData)
        this.$store.dispatch('setWfmPermissions', res.data.wfmPermissions)
        this.$store.dispatch('setWfmStatements', res.data.wfmStatements || [])
        if (res.data?.role?.includes('WFM_ADMIN') && !res.data?.wfmPermissions?.length) {
          localStorage.setItem('needsInitialSetup', 'true')
        }
      } catch (error) {
        console.error(error)
      }
    },

    async fetchLoginUserId() {
      try {
        const response = await this.$axios.get('/user')
        const userData = {
          ...this.$storage.getUniversal('user'),
          _id: response.data?.user?._id
        }
        this.$storage.setUniversal('user', userData)
      } catch (error) {
        console.error(error)
      }
    },
    openComplaintCard() {
      this.isComplaintOpen = !this.isComplaintOpen
    },

    checkNumberValidity() {
      if (!this.form?.mobile) {
        this.isNumberValid = false
        return
      }
      const trimmed = this.form.mobile.trim()
      if (trimmed.includes('@')) {
        this.isEmail = true
        this.isLoginId = false
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        this.isNumberValid = emailRegex.test(trimmed)
      } else if (/^\d{10}$/.test(trimmed)) {
        this.isEmail = false
        this.isLoginId = false
        this.isNumberValid = true
      } else {
        this.isEmail = false
        this.isLoginId = true
        this.isNumberValid = trimmed.length >= 3
      }
      this.form.username = trimmed
    },

    handleGoogleLoginSuccess(googleResponse) {
      // console.log('Google login successful', googleResponse)

      this.isGoogleLoginLoading = true

      this.verifyGoogleTokenWithBackend(googleResponse.token, googleResponse.user)
    },

    // Send Google token to backend for verification
    async verifyGoogleTokenWithBackend(googleToken, googleUser) {
      try {
        this.isLoading = true

        const response = await this.$axios.post('/auth/v3/login', {
          google_token: googleToken
        })

        // Handle the response from your backend
        if (response.status === 200) {
          this.$storage.setUniversal('isAuthenticated', 'true')
          await this.setUser()
          await this.fetchLoginUserId()

          if (
            this.$storage.getUniversal('user').role.includes('WFM_ADMIN') ||
            this.$storage.getUniversal('user').role.includes('WFM_ACCESS')
          ) {
            const needsInitialSetup = localStorage.getItem('needsInitialSetup')
            const hasCompletedSetup = this.$storage.getUniversal('hasCompletedSetup')

            const targetRoute =
              needsInitialSetup === 'true' && !hasCompletedSetup
                ? '/workforce/setup'
                : '/workforce/dashboard'

            this.$router.replace(targetRoute).catch((err) => {
              if (err.name !== 'NavigationDuplicated') {
                console.error('Navigation error:', err)
              }
            })
            this.$toast.success('Google login successful! Welcome ' + googleUser.name)
          } else {
            this.showToast('You are not authorized', 'error')
            localStorage.clear()
          }
        } else {
          throw new Error(response.data.message || 'Authentication failed')
        }
      } catch (error) {
        console.error('Error verifying Google token with backend:', error)
        this.showToast('Google login failed. Please try again.', 'error')
        this.$emit('google-login-error', error)
      } finally {
        this.isLoading = false
        // Reset Google login loading state
        this.isGoogleLoginLoading = false
      }
    },
    handleGuestLogin(creds) {
      this.form.username = creds.username
      this.form.mobile = creds.username
      this.form.passCode = creds.passcode
      localStorage.setItem('organization_code', creds.organization_code)
      this.login()
    }
  },
  beforeDestroy() {
    clearInterval(this.otpTimer)
  }
}
</script>

<style scoped>
.hidden {
  display: none;
}
.gradient {
  background: #5d37bc;
  background: linear-gradient(135deg, #1e3c72, #2a5298);
}
@media (min-width: 1200px) {
  .hidden {
    display: block;
  }
}
.carousel-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.carousel-item-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-full {
  width: 80vmin;
  height: 80vmin;
  object-fit: contain;
  object-position: center;
  text-align: start;
  margin: 0;
  align-self: center;
  justify-self: center;
}

/* Force carousel to take full height */
:deep(.v-carousel) {
  height: 100% !important;
}

:deep(.v-carousel__item) {
  height: 100% !important;
}

.text {
  padding: 30px;
  text-align: center;
}

/* New animations and transitions */
.fade-up-enter-active,
.fade-up-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.fade-up-enter,
.fade-up-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.4s, transform 0.4s;
}
.fade-slide-enter,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

.login-step {
  position: relative;
  padding: 8px 0;
}

.hover-effect {
  transition: all 0.2s ease;
}
.hover-effect:hover {
  opacity: 0.8;
  text-decoration: underline;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(33, 150, 243, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
  }
}

.user-info {
  background-color: rgba(33, 150, 243, 0.05);
  border-radius: 8px;
  padding: 10px;
}
</style>
